<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import { type SuperUserOrganisation as Organisation } from '@/types/generated/types.gen'
  import { OrganisationsService } from '@/types/generated/services.gen'
  import type { FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import LoginAsModal from './LoginAsModal.vue'
  import { LogIn } from 'lucide-vue-next'
  import { ref, watch, nextTick } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'

  const loginAsModalOpen = ref(false)
  const selectedOrganisation = ref<Organisation | null>(null)

  const openLoginAsModal = (org: Organisation) => {
    selectedOrganisation.value = org
    loginAsModalOpen.value = true
  }

  const redirectTo = ref('')
  const filterStripe = ref<string | null>(null)
  const filterHubspot = ref<string | null>(null)
  const filterPhonebot = ref<string | null>(null)
  const filterPlatform = ref<string | null>(null)
  const tableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const booleanFilterOptions = [
    { value: null, title: 'All' },
    { value: 'true', title: 'Yes' },
    { value: 'false', title: 'No' }
  ]

  const phonebotFilterOptions = [
    { value: null, title: 'All' },
    { value: 'self_service', title: 'Self-Service' },
    { value: 'managed', title: 'Managed' }
  ]

  // Helper to get phonebot display text
  const getPhonebotType = (org: Organisation): string | null => {
    if (org.is_phonebot_self_service_customer) {
      return 'Self-Service'
    } else if (org.enable_voice) {
      return 'Managed'
    }
    return null
  }

  // Watch for filter changes and refresh the table
  watch([filterStripe, filterHubspot, filterPhonebot, filterPlatform], async () => {
    await nextTick()
    tableRef.value?.resetAndRefresh()
  })

  const fetchOrganisations = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const response = await OrganisationsService.organisationsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      hasStripe: filterStripe.value === null ? undefined : filterStripe.value === 'true',
      hasHubspot: filterHubspot.value === null ? undefined : filterHubspot.value === 'true',
      phonebotType: filterPhonebot.value === null ? undefined : filterPhonebot.value,
      hasPlatform: filterPlatform.value === null ? undefined : filterPlatform.value === 'true'
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST }
          ]"
        />

        <div class="flex items-center justify-end gap-x-2">
          <router-link
            :to="ROUTES.ADMIN.CUSTOMERS.CUSTOMER_CREATE"
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Create New Customer
          </router-link>
        </div>
      </div>
      <div
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          ref="tableRef"
          :fetchData="fetchOrganisations"
          searchable-fields="Uuid, customer number, customer name"
        >
          <template #actions>
            <div class="flex items-end gap-3">
              <div class="w-[130px]">
                <SelectField
                  v-model="filterStripe"
                  name="filterStripe"
                  label="Stripe"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[130px]">
                <SelectField
                  v-model="filterHubspot"
                  name="filterHubspot"
                  label="HubSpot"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[140px]">
                <SelectField
                  v-model="filterPhonebot"
                  name="filterPhonebot"
                  label="Phonebot"
                  :options="phonebotFilterOptions"
                />
              </div>
              <div class="w-[130px]">
                <SelectField
                  v-model="filterPlatform"
                  name="filterPlatform"
                  label="Platform"
                  :options="booleanFilterOptions"
                />
              </div>
            </div>
          </template>
          <template #table-head>
            <TableCell>UUID</TableCell>
            <TableCell>Customer number</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Health</TableCell>
            <TableCell>Stripe</TableCell>
            <TableCell>HubSpot</TableCell>
            <TableCell>Platform</TableCell>
            <TableCell>Phonebot</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="org in items"
              :key="org.uuid"
            >
              <UniqueTableDataCell :truncate="true">
                <router-link
                  :to="
                    ROUTES.ADMIN.CUSTOMERS.CUSTOMER_DETAIL.replace(
                      ':uuid',
                      (org as Organisation).uuid
                    )
                  "
                >
                  {{ (org as Organisation).uuid }}
                </router-link>
              </UniqueTableDataCell>
              <TableCell>
                {{ (org as Organisation).customer_number }}
              </TableCell>
              <CustomerTableDataCell :org="org as Organisation" />
              <TableCell>
                {{ (org as Organisation).email }}
              </TableCell>
              <TableCell>
                {{ (org as Organisation).vitally_health_score ?? 'N/A' }}
              </TableCell>
              <TableCell>
                <span
                  :class="
                    (org as Organisation).stripe_customer_uuid
                      ? 'text-green-700'
                      : 'text-orange-700'
                  "
                >
                  {{ (org as Organisation).stripe_customer_uuid ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell>
                <span
                  :class="
                    (org as Organisation).hubspot_company_uuid
                      ? 'text-green-700'
                      : 'text-orange-700'
                  "
                >
                  {{ (org as Organisation).hubspot_company_uuid ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell>
                <span
                  :class="(org as Organisation).enable_cases ? 'text-green-700' : 'text-orange-700'"
                >
                  {{ (org as Organisation).enable_cases ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell>
                <span
                  v-if="getPhonebotType(org as Organisation)"
                  :class="
                    (org as Organisation).is_phonebot_self_service_customer
                      ? 'text-emerald-600'
                      : 'text-amber-600'
                  "
                >
                  {{ getPhonebotType(org as Organisation) }}
                </span>
                <span
                  v-else
                  class="text-gray-400"
                >
                  -
                </span>
              </TableCell>
              <TableCell>
                <TableOptionsButton
                  :options="[
                    {
                      label: 'Edit',
                      to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_DETAIL.replace(
                        ':uuid',
                        (org as Organisation).uuid
                      )
                    },
                    {
                      label: 'Login As',
                      icon: LogIn,
                      onClick: () => openLoginAsModal(org as Organisation)
                    },
                    {
                      label: 'Chatbot',
                      to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_BOT_SETTINGS.replace(
                        ':uuid',
                        (org as Organisation).uuid
                      )
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
    <!-- Login As Modal -->
    <LoginAsModal
      v-model:open="loginAsModalOpen"
      :organisation="selectedOrganisation"
    />
  </div>
</template>

<style scoped></style>
